import {
  AppointmentChoiceEligibilityQueryKey,
  AppointmentChoiceEligibility as AppointmentChoiceEligibilityRoute,
  Appointments as AppointmentsRoute,
} from 'api/appointments/routes'
import useAxiosWithAuth from 'api/useAxiosWithAuth'
import { useCallback, useMemo } from 'react'
import { useQuery } from 'react-query'
import { Appointment } from 'types/appointments'
import useAppointmentTypeId from './useAppointmentTypeId'
import useUser from 'hooks/useUser/useUser'
import useActions from 'hooks/useTimeline/useActions'
import {
  generateAppointmentLink,
  generateGenericAppointmentLink,
} from 'utils/appointments.utils'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { BookingEventSource } from 'utils/amplitude.types'

export type AppointmentDefinition = {
  id: string
  title: string
  description: string
  icon: string // URL
  buttonText?: string
}

interface AppointmentEligilibityData {
  hasCompletedInitialRdAppointment: boolean
  hasScheduledInitialRdAppointment: boolean
  hasScheduledInitialMdAppointment: boolean
  hasCompletedSecondMdAppointment: boolean
  hasScheduledSecondMdAppointment: boolean
  apptTypeIds: {
    medicalApptTypeId: string | undefined
    nutritionalApptTypeId: string | undefined
  }
}

interface UseAppointmentsInterface {
  isLoading: boolean
  appointments: Appointment[]
  nonCancelledAppointments: Appointment[]
  hasBookedAtleastTwoMd: boolean
  hasBookedRd: boolean
  isEligibleToChooseAppointmentType: boolean
  isAppointmentChoiceEligibilityLoading: boolean
  hasBookedNumberMDAppointments: (numAppointments: number) => boolean
  getAppointmentCode: (appointment: Appointment) => string
  refetch: () => void
  appointmentEligibilityData: AppointmentEligilibityData
  getAppointmentLink: (source: BookingEventSource) => string | undefined
  getUpcomingAppointments: (limit?: number) => Appointment[]
}

const useAppointments = (): UseAppointmentsInterface => {
  const {
    appointmentTypeId: followupMdAppointmentTypeId,
  } = useAppointmentTypeId('Followup MD')
  const {
    appointmentTypeId: followupRdAppointmentTypeId,
  } = useAppointmentTypeId('Followup RD')
  const {
    appointmentTypeId: initialMdAppointmentTypeId,
  } = useAppointmentTypeId('Initial MD')
  const {
    appointmentTypeId: initialRdAppointmentTypeId,
  } = useAppointmentTypeId('Initial RD')

  const { appointmentTypeId: secondMdAppointmentTypeId } = useAppointmentTypeId(
    'Second MD'
  )

  const { appointmentTypeId: careTransferMdTypeId } = useAppointmentTypeId(
    'Care Transfer MD'
  )

  const { user } = useUser()
  const { doesPatientHaveCareTransfer } = useActions()

  const axiosWithAuth = useAxiosWithAuth()

  const fetchAppointments = async (): Promise<Appointment[]> => {
    try {
      const res = await axiosWithAuth.get<Appointment[]>(AppointmentsRoute)
      return res.data
    } catch (err) {
      console.error('Failed to fetch appointments:', err)
      return []
    }
  }

  const { isLoading, data, refetch } = useQuery(
    AppointmentsRoute,
    fetchAppointments
  )

  const appointments = useMemo(() => data ?? [], [data])

  const fetchAppointmentChoiceEligibility = async (): Promise<boolean> => {
    return await axiosWithAuth
      .get<boolean>(AppointmentChoiceEligibilityRoute)
      .then((res) => res.data)
      .catch(() => false)
  }

  const {
    data: isEligibleToChooseAppointmentTypeData,
    isLoading: isAppointmentChoiceEligibilityLoading,
  } = useQuery(
    AppointmentChoiceEligibilityQueryKey,
    fetchAppointmentChoiceEligibility
  )

  const isEligibleToChooseAppointmentType =
    isEligibleToChooseAppointmentTypeData ?? false

  const nonCancelledAppointments = useMemo(
    () => appointments.filter((appointment) => !appointment.isCancelled),
    [appointments]
  )

  const hasBookedAtleastTwoMd = useMemo(
    () =>
      appointments.filter(
        (a) => a.provider.type === 'Medical Provider' && !a.isCancelled
      ).length > 1,
    [appointments]
  )

  const hasBookedNumberMDAppointments = (numAppointments: number) =>
    appointments.filter(
      (a) => a.provider.type === 'Medical Provider' && !a.isCancelled
    ).length > numAppointments

  const hasBookedRd = useMemo(
    () =>
      appointments.filter(
        (a) => a.provider.type === 'Dietician' && !a.isCancelled
      ).length > 0,
    [appointments]
  )

  const getAppointmentCode = useCallback((appointment: Appointment) => {
    if (appointment.isCancelled) {
      return 'cancelled'
    } else if (new Date(appointment.time) > new Date()) {
      return 'scheduled'
    } else {
      return 'complete'
    }
  }, [])

  const appointmentFilter = useCallback(
    (
      appointment: Appointment,
      type: string,
      category: string,
      code: string
    ) => {
      return (
        appointment.provider.type === type &&
        appointment.type.category === category &&
        getAppointmentCode(appointment) === code
      )
    },
    [getAppointmentCode]
  )

  const appointmentEligibilityData = useMemo(() => {
    const hasCompletedInitialRdAppointment = appointments.some((appointment) =>
      appointmentFilter(appointment, 'Dietician', 'Initial RD', 'complete')
    )
    const hasScheduledInitialRdAppointment = appointments.some((appointment) =>
      appointmentFilter(appointment, 'Dietician', 'Initial RD', 'scheduled')
    )

    const hasScheduledInitialMdAppointment = appointments.some((appointment) =>
      appointmentFilter(
        appointment,
        'Medical Provider',
        'Initial MD',
        'scheduled'
      )
    )

    const hasCompletedInitialMdAppointment = appointments.some((appointment) =>
      appointmentFilter(
        appointment,
        'Medical Provider',
        'Initial MD',
        'complete'
      )
    )

    const hasCompletedSecondMdAppointment = appointments.some((appointment) =>
      appointmentFilter(
        appointment,
        'Medical Provider',
        'Second MD',
        'complete'
      )
    )

    const hasScheduledSecondMdAppointment = appointments.some((appointment) =>
      appointmentFilter(
        appointment,
        'Medical Provider',
        'Second MD',
        'scheduled'
      )
    )

    const hasScheduledCareTransferMdAppointment = appointments.some(
      (appointment) =>
        appointmentFilter(
          appointment,
          'Medical Provider',
          'Care Transfer MD',
          'scheduled'
        )
    )

    const hasCompletedCareTransferMdAppointment = appointments.some(
      (appointment) =>
        appointmentFilter(
          appointment,
          'Medical Provider',
          'Care Transfer MD',
          'complete'
        )
    )

    const getMedicalApptTypeId = (): string | undefined => {
      // if one of patient's active actions is book-transfer-md and
      // if they have not scheduled care transfer MD appt, they are eligible to book care transfer MD
      if (
        doesPatientHaveCareTransfer &&
        !hasScheduledCareTransferMdAppointment
      ) {
        return careTransferMdTypeId
      }

      // if they have scheduled a care transfer md but not completed it,
      // they are not eligible to book any MD
      if (
        hasScheduledCareTransferMdAppointment &&
        !hasCompletedCareTransferMdAppointment
      ) {
        return undefined
      }

      // if they have completed second MD or care transfer MD, they are eligible to book follow up MD
      if (
        hasCompletedSecondMdAppointment ||
        hasCompletedCareTransferMdAppointment
      ) {
        return followupMdAppointmentTypeId
      }

      // SMV scheduled, so not eligible to book follow up MD
      if (hasScheduledSecondMdAppointment) {
        return undefined
      }

      // initial MD completed, so SMV eligible
      if (hasCompletedInitialMdAppointment) {
        return secondMdAppointmentTypeId
      }

      // initial MD scheduled, so not eligible to book SMV
      if (hasScheduledInitialMdAppointment) {
        return undefined
      }

      // only eligible to schedule initial MD
      return initialMdAppointmentTypeId
    }

    const getNutritionalApptTypeId = (): string | undefined => {
      // initial RD completed, so eligible to book follow up RD
      if (hasCompletedInitialRdAppointment) {
        return followupRdAppointmentTypeId
      }

      // initial RD scheduled, so not follow up RD eligible
      if (hasScheduledInitialRdAppointment) {
        return undefined
      }

      // only eliigble for initial RD
      return initialRdAppointmentTypeId
    }

    return {
      hasCompletedInitialRdAppointment,
      hasScheduledInitialRdAppointment,
      hasCompletedInitialMdAppointment,
      hasScheduledInitialMdAppointment,
      hasCompletedSecondMdAppointment,
      hasScheduledSecondMdAppointment,

      apptTypeIds: {
        medicalApptTypeId:
          user?.stripeCustomer?.product?.type === 'nutrition-only'
            ? undefined
            : getMedicalApptTypeId(),
        nutritionalApptTypeId: getNutritionalApptTypeId(),
      },
    }
  }, [
    user,
    appointments,
    appointmentFilter,
    initialMdAppointmentTypeId,
    followupMdAppointmentTypeId,
    initialRdAppointmentTypeId,
    followupRdAppointmentTypeId,
    secondMdAppointmentTypeId,
    careTransferMdTypeId,
    doesPatientHaveCareTransfer,
  ])

  const getAppointmentLink = useCallback(
    (source: BookingEventSource) => {
      // if the feature flag is enabled

      const { apptTypeIds } = appointmentEligibilityData

      if (!apptTypeIds) {
        return undefined
      }

      const { medicalApptTypeId, nutritionalApptTypeId } = apptTypeIds

      // Convert to boolean values
      const hasMedical = !!medicalApptTypeId
      const hasNutritional = !!nutritionalApptTypeId

      // if they are eligible to book any kind of MD or RD appt OR if they are not eligible to book either
      // take them to the appointment type select screen
      if (hasMedical === hasNutritional) {
        return `/schedule?source=${source}`
      }

      // if only eligible to book a MD appt
      if (apptTypeIds.medicalApptTypeId) {
        // if follow up MD, generate a generic appointment link
        if (apptTypeIds.medicalApptTypeId === followupMdAppointmentTypeId) {
          return generateGenericAppointmentLink(apptTypeIds.medicalApptTypeId, {
            source: source,
            patient_action_id: null,
            patient_action_definition: null,
            resource_id: null,
          })
        }

        return generateAppointmentLink(apptTypeIds.medicalApptTypeId, {
          source: source,
          patient_action_id: null,
          patient_action_definition: null,
          resource_id: null,
        })
      }

      // if only eligible to book a RD appt
      if (apptTypeIds.nutritionalApptTypeId) {
        // if follow up RD, generate a generic appt link
        if (apptTypeIds.nutritionalApptTypeId === followupRdAppointmentTypeId) {
          return generateGenericAppointmentLink(
            apptTypeIds.nutritionalApptTypeId,
            {
              source: source,
              patient_action_id: null,
              patient_action_definition: null,
              resource_id: null,
            }
          )
        }

        return generateAppointmentLink(apptTypeIds.nutritionalApptTypeId, {
          source: source,
          patient_action_id: null,
          patient_action_definition: null,
          resource_id: null,
        })
      }

      return undefined
    },
    [
      appointmentEligibilityData,
      hasBookedAtleastTwoMd,
      user,
      followupMdAppointmentTypeId,
      followupRdAppointmentTypeId,
    ]
  )

  const getUpcomingAppointments = useCallback(
    (limit = 2) => {
      return appointments
        .filter(
          (appointment) =>
            getAppointmentCode(appointment) === 'scheduled' &&
            appointment.type.id !== followupMdAppointmentTypeId &&
            appointment.type.id !== followupRdAppointmentTypeId
        )
        .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime())
        .slice(0, limit)
    },
    [appointments, getAppointmentCode]
  )

  return {
    isLoading,
    appointments,
    nonCancelledAppointments,
    refetch,
    hasBookedRd,
    hasBookedAtleastTwoMd,
    isEligibleToChooseAppointmentType,
    isAppointmentChoiceEligibilityLoading,
    hasBookedNumberMDAppointments,
    getAppointmentCode,
    appointmentEligibilityData,
    getAppointmentLink,
    getUpcomingAppointments,
  }
}

export default useAppointments
